import { useDebounce } from '@vueuse/core';
import { useExperienceApi } from '@/shared/api/useExperienceApi';
import { TOTAL_COUNT_REQUEST_DEBOUNCE } from '../../const/config';
import type { TTourFiltersPageState, TTourFiltersState } from '../../types/tour/state';
import { buildTourExperiencesCountAlignedQuery } from '../../utils/tour/TourBuildExperiencesCountAlignedQuery';
import { useUnlicensedContentFilter } from '@/shared/composables/useUnlicensedContentFilter.ts';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';
import { useDraftDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayedCurrency.ts';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';

interface UseTourFiltersTotalCountOptions {
  useDraftDisplayCurrency?: boolean;
}

export function useTourFiltersTotalCount(
  state: TTourFiltersState,
  pageParams: TTourFiltersPageState,
  options: UseTourFiltersTotalCountOptions = {},
) {
  const { getExperiencesCount } = useExperienceApi();
  const { hideUnlicensed } = useUnlicensedContentFilter();
  const displayCurrencyQueryParams = options.useDraftDisplayCurrency
    ? useDraftDisplayCurrencyQueryParams()
    : useDisplayCurrencyQueryParams();

  const finalQueryParams = computed(() => ({
    ...buildTourExperiencesCountAlignedQuery(state, pageParams),
    ...displayCurrencyQueryParams.value,
    exclude_needs_license: hideUnlicensed.value,
  }));

  const debouncedQueryParams = useDebounce(finalQueryParams, TOTAL_COUNT_REQUEST_DEBOUNCE);

  const queryKey = computed(() => {
    return ['tourExperiencesTotalCount', JSON.stringify(debouncedQueryParams.value)];
  });

  const {
    data: totalCount,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => {
      return getExperiencesCount({ query: debouncedQueryParams.value });
    },
    placeholderData: keepPreviousData,
    enabled: computed(() =>
      Boolean(
        debouncedQueryParams.value.city__slug ||
        debouncedQueryParams.value.region__slug ||
        debouncedQueryParams.value.country__slug,
      ),
    ),
    select: (response) => response.data?.count ?? 0,
    staleTime: 1000 * 60 * 5, // 5 минут
  });

  return {
    isLoading: computed(() => isLoading.value || isFetching.value),
    totalCount,
  };
}
